import styles from './DeclinedState.module.scss';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  InlineNotification,
  NotificationType,
  TextArea,
} from '@spotted-zebra-uk/ui-components';
import { DECLINE_REASON_MAX_LENGTH } from '../../api/bookingApi.types';

interface DeclinedStateProps {
  sending: boolean;
  /** Why the last attempt to send failed, if it did. */
  notice?: string | null;
  /**
   * Set when declining cancels an interview already booked (a reschedule
   * offer): what is cancelled, said before the candidate sends.
   */
  cancelWarning?: string;
  onSend: (reason: string) => void;
  onBack: () => void;
}

/** Decline-all with a free-text note on general availability. */
const DeclinedState: FC<DeclinedStateProps> = ({
  sending,
  notice,
  cancelWarning,
  onSend,
  onBack,
}) => {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');

  return (
    <div className={styles.wrapper}>
      <div className={styles.heading}>
        <h1 className={styles.title}>{t('publicScheduling.declineTitle')}</h1>
        <p className={styles.subline}>{t('publicScheduling.declineSubline')}</p>
      </div>
      <div className={styles.field}>
        <label htmlFor="candidate-availability" className={styles.label}>
          {t('publicScheduling.availabilityLabel')}
        </label>
        <TextArea
          id="candidate-availability"
          value={reason}
          rows={4}
          maxLength={DECLINE_REASON_MAX_LENGTH}
          maxCharacters={DECLINE_REASON_MAX_LENGTH}
          placeholder={t('publicScheduling.availabilityPlaceholder')}
          onChange={setReason}
        />
      </div>
      {cancelWarning && (
        <InlineNotification notificationType={NotificationType.WARNING}>
          <p>{cancelWarning}</p>
        </InlineNotification>
      )}
      {notice && (
        <InlineNotification notificationType={NotificationType.ERROR}>
          <p>{notice}</p>
        </InlineNotification>
      )}
      <div className={styles.actions}>
        <Button
          variant="primary"
          size="medium"
          disabled={sending}
          loading={sending}
          onClick={() => onSend(reason)}
        >
          {cancelWarning
            ? t('publicScheduling.rescheduleDeclineSend')
            : t('publicScheduling.sendToHiringTeam')}
        </Button>
        <Button variant="tertiary" size="medium" onClick={onBack}>
          {t('publicScheduling.lookAtTimesAgain')}
        </Button>
      </div>
    </div>
  );
};

export default DeclinedState;
